import { useMemo, useCallback, useState } from "react";
import {
  Box,
  Flex,
  Text,
  ScrollArea,
  TextField,
  Select,
  Button,
  IconButton,
} from "@radix-ui/themes";
import { MagnifyingGlassIcon, ChevronLeftIcon } from "@radix-ui/react-icons";
import { useQuery } from "react-query";

import styles from "../../styles.module.scss";

import { TopicWithBadges } from "./TopicsList.types";
import { TopicListItem } from "./TopicListItem";
import { EmptyState } from "./EmptyState";
import { Topic } from "@/v2/entities/topic/Topic.model";
import { useReleaseConversationApi } from "@/v2/entities/release-conversation/ReleaseConversation.api";
import {
  ReleaseConversationFilters,
  ReleaseConversation,
} from "@/v2/entities/release-conversation/ReleaseConversation.model";
import { InteractionType } from "@/routes/simulator-live-mode/types";
import { useSimulator } from "../../context/SimulatorWrapper";
import { TrainingPanelInteractionCard } from "./TrainingPanelInteractionCard";
import { InteractionListView, InteractionListItems } from "./shared";
import {
  TopicType,
  NavigateToTranscriptCallback,
} from "./TopicsList.shared.types";

interface TopicsTabProps {
  topics: TopicWithBadges[];
  selectedTopic: Topic | null;
  onTopicSelect: (topic: Topic | null) => void;
  onNavigateToSessions: (topic: Topic) => void;
  onNavigateToTranscript: NavigateToTranscriptCallback;
  topicsList?: Array<{ id: number; topic: string }>;
  isDisabled: boolean;
  selectedTopicForSessions: Topic | null;
  onBackFromSessions: () => void;
}

export const TopicsTab = ({
  topics,
  selectedTopic,
  onTopicSelect,
  onNavigateToSessions,
  onNavigateToTranscript,
  topicsList,
  isDisabled,
  selectedTopicForSessions,
  onBackFromSessions,
}: TopicsTabProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState<TopicType>("all");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const limit = 50;
  const releaseConversationApi = useReleaseConversationApi();
  const { releasesWithVersions, topics: topicsListFromSimulator } =
    useSimulator();

  const filteredTopics = useMemo(() => {
    return topics.filter((topicWithBadges: TopicWithBadges) => {
      const matchesSearch = topicWithBadges.topicName
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

      let matchesType = true;
      if (selectedType === "live") {
        matchesType = topicWithBadges.isLive;
      } else if (selectedType === "updates") {
        matchesType = topicWithBadges.hasUpdates;
      } else if (selectedType === "new") {
        matchesType = topicWithBadges.isNew;
      } else if (selectedType === "untrained") {
        matchesType =
          !topicWithBadges.isLive &&
          !topicWithBadges.hasUpdates &&
          !topicWithBadges.isNew;
      }

      return matchesSearch && matchesType;
    });
  }, [topics, searchQuery, selectedType]);

  const filterOptions = [
    { value: "all", label: "All Statuses" },
    { value: "live", label: "Live" },
    { value: "new", label: "New" },
    { value: "updates", label: "Has Updates" },
    { value: "untrained", label: "Untrained" },
  ];

  const handleTopicClick = useCallback(
    (topicName: string) => {
      if (!topicsListFromSimulator) return;

      const topicObj = topicsListFromSimulator.find(
        (t) => t.topic === topicName
      );
      if (topicObj) {
        onNavigateToSessions(topicObj);
        onTopicSelect(topicObj);
      } else {
        onTopicSelect(null);
      }
    },
    [topicsListFromSimulator, onNavigateToSessions, onTopicSelect]
  );

  const handleBackToTopics = useCallback(() => {
    onBackFromSessions();
    setPage(1);
    setSearch("");
  }, [onBackFromSessions]);

  const topicIds = useMemo(() => {
    if (selectedTopicForSessions) {
      return [selectedTopicForSessions.id];
    }
    return [];
  }, [selectedTopicForSessions]);

  const filters: ReleaseConversationFilters = useMemo(() => {
    const last20Releases = releasesWithVersions?.slice(-20) || [];
    const baseFilters: ReleaseConversationFilters = {
      page,
      limit,
      releaseIds: last20Releases?.map((r) => r.id) || [],
      topicIds: topicIds.length > 0 ? topicIds : undefined,
    };

    if (search) {
      baseFilters.commentFilter = search;
    }

    return baseFilters;
  }, [page, limit, topicIds, search, releasesWithVersions]);

  const { data: sessionsData, isLoading: isLoadingSessions } = useQuery({
    queryKey: ["release-conversations", filters, selectedTopicForSessions?.id],
    queryFn: () => releaseConversationApi.getReleaseConversations(filters),
    enabled: !!selectedTopicForSessions,
  });

  const getInteractionType = useCallback((session: ReleaseConversation) => {
    if (session.ticketId || session.ticketInTrainingId) {
      return "tickets";
    }
    if (session.conversationId || session.conversationInTrainingId) {
      return "conversations";
    }
    return null;
  }, []);

  const handleSessionClick = useCallback(
    (session: ReleaseConversation) => {
      const type = getInteractionType(session);
      if (!type) {
        return;
      }
      const interactionType =
        type === "tickets"
          ? InteractionType.TICKET
          : InteractionType.CONVERSATION;
      onNavigateToTranscript(
        session.trainingSessionId,
        `session-${JSON.stringify(session)}`,
        interactionType
      );
    },
    [onNavigateToTranscript, getInteractionType]
  );

  const handleLoadMore = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const hasMorePages = useMemo(() => {
    return (
      sessionsData?.pagination &&
      sessionsData.pagination.page < sessionsData.pagination.totalPages
    );
  }, [sessionsData?.pagination]);

  const isPendingReview = useCallback((session: ReleaseConversation) => {
    return !!(session.ticketInTrainingId || session.conversationInTrainingId);
  }, []);

  if (selectedTopicForSessions) {
    return (
      <Flex direction="column" style={{ height: "100%", flex: 1 }}>
        <Flex direction="column" gap="2" p="2" pt="4" align="start">
          <IconButton variant="ghost" size="2" onClick={handleBackToTopics}>
            <ChevronLeftIcon />
          </IconButton>
          <Text size="4" weight="medium" color="gray" highContrast>
            {selectedTopicForSessions.topic}
          </Text>
        </Flex>
        <InteractionListView
          activeFilters={{}}
          setActiveFilters={() => {}}
          filter={undefined}
          isLoading={isLoadingSessions}
          isEmpty={
            !sessionsData?.conversations ||
            sessionsData.conversations.length === 0
          }
          loadingText="Loading sessions..."
          emptyText="No sessions found"
          search={search}
          onSearchChanged={(value) => {
            setSearch(value);
            setPage(1);
          }}
          showSearchAndFilters={false}
          allowedFilterFields={[]}
          customFilterOptions={{}}
        >
          {sessionsData?.conversations &&
            sessionsData.conversations.length > 0 && (
              <>
                <InteractionListItems>
                  {sessionsData.conversations.map((session) => (
                    <TrainingPanelInteractionCard
                      key={session.id}
                      interaction={{
                        interactionType: "conversations",
                        id: session.trainingSessionId,
                        displayId: session.quackUserId
                          ? `${session.id} By ${session.quackUserId}`
                          : `${session.id}`,
                        subject:
                          session.releaseComments?.[0]?.comment ||
                          session.feedbackComment ||
                          `Session ${session.trainingSessionId}`,
                        description:
                          session.releaseComments?.[0]?.comment ||
                          session.feedbackComment ||
                          `Session ${session.trainingSessionId}`,
                        topic: session?.topicId
                          ? topicsListFromSimulator?.find(
                              (t: Topic) => t.id === session.topicId
                            )?.topic || ""
                          : "",
                        category:
                          session.feedback !== "NONE" ? session.feedback : "",
                        createdAt: session.createdAt,
                        updatedAt: session.updatedAt,
                        status: "ACTIVE",
                        channel: "simulator",
                        countPublicMessages:
                          session.releaseComments?.length || 0,
                        countInternalMessages: 0,
                      }}
                      submitter={`Version ${
                        releasesWithVersions?.find(
                          (r) => r.id === session.releaseId
                        )?.version || ""
                      }`}
                      interactionType={getInteractionType(session)}
                      onClick={() => handleSessionClick(session)}
                      isPendingReview={isPendingReview(session)}
                    />
                  ))}
                </InteractionListItems>

                {hasMorePages && (
                  <Button
                    variant="soft"
                    onClick={handleLoadMore}
                    disabled={isLoadingSessions}
                  >
                    {isLoadingSessions ? "Loading..." : "Load More"}
                  </Button>
                )}
              </>
            )}
        </InteractionListView>
      </Flex>
    );
  }

  if (!topics.length) {
    return (
      <Box className={styles.topicsListContainer}>
        <Text size="2" color="gray">
          No topics available
        </Text>
      </Box>
    );
  }

  return (
    <Flex direction="column" className="h-full">
      <Flex direction="column" gap="3" className="p-2">
        <Flex gap="2">
          <TextField.Root
            size="2"
            variant="soft"
            color="gray"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search topics..."
            style={{ flex: 1 }}
          >
            <TextField.Slot>
              <MagnifyingGlassIcon height="16" width="16" />
            </TextField.Slot>
          </TextField.Root>

          <Select.Root
            value={selectedType}
            onValueChange={(value) => setSelectedType(value as TopicType)}
          >
            <Select.Trigger style={{ width: "140px" }} />
            <Select.Content position="popper" sideOffset={5}>
              <Select.Group>
                {filterOptions.map((option) => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select.Group>
            </Select.Content>
          </Select.Root>
        </Flex>
      </Flex>

      <ScrollArea className="flex-1">
        <Flex direction="column" gap="2">
          {filteredTopics.length > 0 ? (
            filteredTopics.map((topicWithBadges: TopicWithBadges) => (
              <TopicListItem
                key={topicWithBadges.topicName}
                topic={topicWithBadges.topicName}
                badges={topicWithBadges.badges}
                isSelected={selectedTopic?.topic === topicWithBadges.topicName}
                onSelect={() => handleTopicClick(topicWithBadges.topicName)}
                disabled={isDisabled}
                isCollapsed={false}
              />
            ))
          ) : (
            <EmptyState
              searchQuery={searchQuery}
              selectedType={selectedType}
              isCollapsed={false}
            />
          )}
        </Flex>
      </ScrollArea>
    </Flex>
  );
};
